import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Eye, 
  MessageSquare, 
  ThumbsUp, 
  Share2, 
  TrendingUp, 
  Users, 
  Clock, 
  Activity,
  BarChart3,
  PieChart as PieChartIcon
} from 'lucide-react';
import { AnalyticsService, AnalyticsOverview } from '@/services/analytics/analyticsService';

interface AnalyticsDashboardProps {
  timeRange?: number; // days
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ 
  timeRange = 30 
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [publishingMetrics, setPublishingMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange);

  useEffect(() => {
    loadAnalytics();
  }, [selectedTimeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsData, publishingData] = await Promise.all([
        AnalyticsService.getAnalyticsOverview(selectedTimeRange),
        AnalyticsService.getPublishingMetrics(selectedTimeRange)
      ]);
      
      setAnalytics(analyticsData);
      setPublishingMetrics(publishingData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Kunne ikke laste analysedata.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overviewCards = [
    {
      title: 'Totale visninger',
      value: analytics.totalViews.toLocaleString(),
      icon: Eye,
      description: `Siste ${selectedTimeRange} dager`,
      trend: '+12%'
    },
    {
      title: 'Publiserte intervjuer',
      value: analytics.totalInterviews.toLocaleString(),
      icon: MessageSquare,
      description: 'Totalt publisert',
      trend: '+5%'
    },
    {
      title: 'Kommentarer',
      value: analytics.totalComments.toLocaleString(),
      icon: MessageSquare,
      description: 'Brukerengasjement',
      trend: '+8%'
    },
    {
      title: 'Debatter',
      value: analytics.totalDebates.toLocaleString(),
      icon: Users,
      description: 'Aktive diskusjoner',
      trend: '+15%'
    }
  ];

  const engagementData = [
    { name: 'Gjennomsnittlig øktvarighet', value: Math.round(analytics.userEngagement.averageSessionDuration), unit: 'min' },
    { name: 'Sprungrate', value: Math.round(analytics.userEngagement.bounceRate), unit: '%' },
    { name: 'Sider per økt', value: analytics.userEngagement.pagesPerSession.toFixed(1), unit: '' }
  ];

  const publishingData = publishingMetrics ? [
    { name: 'Publisert', value: publishingMetrics.published, color: 'hsl(var(--primary))' },
    { name: 'Avvist', value: publishingMetrics.rejected, color: 'hsl(var(--destructive))' },
    { name: 'I kø', value: publishingMetrics.queued, color: 'hsl(var(--secondary))' }
  ] : [];

  return (
    <div className="p-6 space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Analytisk oversikt</h1>
          <p className="text-muted-foreground">Detaljert innsikt i plattformens ytelse</p>
        </div>
        <Tabs value={selectedTimeRange.toString()} onValueChange={(value) => setSelectedTimeRange(parseInt(value))}>
          <TabsList>
            <TabsTrigger value="7">7 dager</TabsTrigger>
            <TabsTrigger value="30">30 dager</TabsTrigger>
            <TabsTrigger value="90">90 dager</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{card.description}</p>
                <Badge variant="secondary" className="text-xs">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {card.trend}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Analytics */}
      <Tabs defaultValue="traffic" className="space-y-4">
        <TabsList>
          <TabsTrigger value="traffic">Trafikk & Engagement</TabsTrigger>
          <TabsTrigger value="content">Innholdsytelse</TabsTrigger>
          <TabsTrigger value="publishing">Publiseringsanalyse</TabsTrigger>
          <TabsTrigger value="users">Brukeradferd</TabsTrigger>
        </TabsList>

        <TabsContent value="traffic" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Daily Views Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Daglige visninger
                </CardTitle>
                <CardDescription>Visningstrend over tid</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.dailyViews}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="views" 
                      stroke="hsl(var(--primary))" 
                      fill="hsl(var(--primary))" 
                      fillOpacity={0.6} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* User Engagement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Brukerengasjement
                </CardTitle>
                <CardDescription>Viktige engasjementsmålinger</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {engagementData.map((metric, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm font-medium">{metric.name}</span>
                    <div className="flex items-center gap-1">
                      <span className="text-2xl font-bold">{metric.value}</span>
                      <span className="text-sm text-muted-foreground">{metric.unit}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Topp innhold</CardTitle>
              <CardDescription>Mest populære innhold basert på visninger</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analytics.topContent.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="title" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="views" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="publishing" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5" />
                  Publiseringsstatus
                </CardTitle>
                <CardDescription>Fordeling av publiseringshandlinger</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={publishingData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {publishingData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Publiseringsstatistikk</CardTitle>
                <CardDescription>Viktige publiseringsmålinger</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {publishingMetrics && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Publiseringsrate</span>
                      <span className="text-2xl font-bold">
                        {publishingMetrics.publishRate.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Totalt behandlet</span>
                      <span className="text-2xl font-bold">{publishingMetrics.total}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">I behandling</span>
                      <span className="text-2xl font-bold">{publishingMetrics.queued}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Brukeradferd
              </CardTitle>
              <CardDescription>Detaljert brukeraktivitet og adferdsanalyse</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Activity className="h-12 w-12 mx-auto text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Gå til brukeraktivitet</h3>
                <p className="text-muted-foreground mb-4">
                  Se detaljert brukeraktivitet, økter og adferdsanalyse.
                </p>
                <a 
                  href="/analytics/activity" 
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                >
                  Åpne brukeraktivitet
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};